import AddressFormatter from "@shopify/address";
import chunk from "lodash/chunk";
import extend from "lodash/extend";
import find from "lodash/find";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import join from "lodash/join";
import map from "lodash/map";
import split from "lodash/split";
import uniqBy from "lodash/uniqBy";
import type { intlFormatPropTypes } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import Contracts from "api/api-contracts";
import { Fees } from "api/api-fees";
import { Invoices } from "api/api-invoices";

import { fetchAddressById } from "../../../app-main/actions/fee-profile";
import { selectEnvWeb } from "../../../app-main/selectors/env.selectors";
import {
  selectInvoiceFeeProfile,
  selectVatBusinessName,
} from "../../../app-main/selectors/fee-profile.selectors";
import { selectFeeTypes } from "../../../app-main/selectors/fees.selectors";
import {
  selectFirstName,
  selectLastName,
  selectProfileData,
  selectProfileOrganization,
} from "../../../app-main/selectors/profile.selectors";
import TaxInvoice from "../../../shared-components/invoice/TaxInvoice";
import { SalesTaxInvoice } from "../../taxes/invoice/SalesTaxInvoice/SalesTaxInvoice";

import {
  CONTRACT_LOOKUP_CHUNK_SIZE,
  EU_ADDRESS_COUNTRY,
  EU_REGION_CODE,
  SALES_TAX_ENTRY_TYPE,
  US_SALES_TAX_FEE_NAME,
} from "./TaxInvoices.consts";
import type {
  ContractsResponse,
  InvoiceDownloadRequest,
  InvoiceEntriesResponse,
  InvoiceEntry,
  RegionalFeesResponse,
} from "./TaxInvoices.types";
import { downloadPdf, getTaxDetails, revealPdfPage } from "./TaxInvoices.utils";

type UseInvoiceDownloadsOptions = {
  intl: intlFormatPropTypes;
  locale: string;
  token: string;
};

/**
 * Builds and downloads an invoice as a PDF.
 */
const useInvoiceDownloads = ({
  intl,
  locale,
  token,
}: UseInvoiceDownloadsOptions) => {
  const dispatch = useDispatch();

  const envWeb = useSelector(selectEnvWeb);
  const feeTypes = useSelector(selectFeeTypes);
  const firstName = useSelector(selectFirstName);
  const lastName = useSelector(selectLastName);
  const profileData = useSelector(selectProfileData);
  const userFeeProfile = useSelector(selectInvoiceFeeProfile);
  const userOrganization = useSelector(selectProfileOrganization);
  const vatBusinessName = useSelector(selectVatBusinessName);

  const fetchEntries = (invoiceId: string) =>
    new Promise<InvoiceEntry[]>((resolve) => {
      Invoices.fetchInvoiceEntries(
        token,
        invoiceId,
        (response: InvoiceEntriesResponse) => {
          if (response.statusCode === 200) {
            resolve(response.entries);
          }
        },
      );
    });

  /** The fee profile an invoice was issued against. */
  const fetchInvoiceAddress = (entries: InvoiceEntry[]) => {
    const feeProfileId =
      (entries[0]?.fee_profile_id ?? null) ||
      (entries[0]?.billing_profile_id ?? null);

    return dispatch(fetchAddressById(token, feeProfileId));
  };

  const downloadSalesTaxInvoice = async ({
    countryName,
    day,
    feeName,
    invoiceId,
    month,
    year,
  }: InvoiceDownloadRequest) => {
    const entries = await fetchEntries(invoiceId);

    const spaceRentalEntry = get(
      find(
        entries,
        (entry) => SALES_TAX_ENTRY_TYPE.spaceRental === entry.type,
      ) || {},
      "metadata",
      {},
    );
    const addonsEntry = get(
      find(entries, (entry) => SALES_TAX_ENTRY_TYPE.addOns === entry.type) ||
        {},
      "metadata",
      {},
    );

    const controlId = entries[0]?.control_id ?? null;
    const contracts = await new Promise<ContractsResponse>((resolve) => {
      Contracts.readContracts(
        controlId,
        token,
        (response: ContractsResponse) => {
          resolve(response);
        },
      );
    });

    if (contracts.statusCode !== 200) {
      return;
    }

    const guestFeeProfile = (await fetchInvoiceAddress(entries)) || {};

    const addressFormatter = new AddressFormatter(split(locale, "-", 1)[0]);
    const guestAddressFormat = await addressFormatter.format({
      address1: guestFeeProfile.street,
      address2: guestFeeProfile.unit,
      province: guestFeeProfile.state,
      city: guestFeeProfile.city,
      zip: guestFeeProfile.zip,
      country: guestFeeProfile.country,
    });

    const guestDetails = {
      usersFullName: `${firstName ?? ""} ${lastName ?? ""}`,
      userAddressFormatArray: guestAddressFormat,
      userVatId: userFeeProfile?.vat_id ?? null,
      businessName: vatBusinessName,
      userOrganization,
      invoiceFeeName: feeName,
    };

    const { taxNumberName, taxNumber, contactPhoneNumber } = getTaxDetails(
      "US",
      envWeb,
    );

    const document = (
      <SalesTaxInvoice
        invoiceId={invoiceId}
        date={`${month}${day ? ` ${day}` : ""}, ${year}`}
        invoiceCountry={countryName}
        invoiceFeeName={feeName}
        guestDetails={guestDetails}
        spaceRentalEntry={spaceRentalEntry}
        addonsEntry={addonsEntry}
        contracts={contracts.contracts}
        taxNumberName={taxNumberName}
        taxNumber={taxNumber}
        contactPhoneNumber={contactPhoneNumber}
      />
    );

    await downloadPdf(
      document,
      `PeerspaceSalesTaxInvoice-${invoiceId}.pdf`,
      revealPdfPage,
    );
  };

  /** Space rentals behind an invoice, looked up in batches. */
  const fetchSpaceRentals = async (entries: InvoiceEntry[]) => {
    const chunks = chunk(
      uniqBy(entries, "control_id"),
      CONTRACT_LOOKUP_CHUNK_SIZE,
    );

    const responses = await Promise.all(
      chunks.map((entryChunk) => {
        return new Promise((resolve) => {
          const query = join(
            map(entryChunk, (entry) => get(entry, "control_id")),
            ",",
          );
          Contracts.fetchSpaceRentalsInfo(
            token,
            query ? `?control_ids=${query}` : "",
            (spaceRentals: unknown) => {
              resolve(spaceRentals);
            },
          );
        });
      }),
    );

    return extend({}, ...responses);
  };

  const downloadTaxInvoice = async ({
    countryName,
    day,
    feeName,
    invoiceId,
    isTransactional,
    month,
    year,
  }: InvoiceDownloadRequest) => {
    const entries = await fetchEntries(invoiceId);
    const spaceRentals = await fetchSpaceRentals(entries);

    const entriesGroupedByType = groupBy(entries, "txn_type");
    const invoiceEntriesByType: Record<string, { entries: InvoiceEntry[] }> =
      {};
    if (!isEmpty(entriesGroupedByType.CHARGE)) {
      invoiceEntriesByType.CHARGE = { entries: entriesGroupedByType.CHARGE };
    }
    if (!isEmpty(entriesGroupedByType.REFUND)) {
      invoiceEntriesByType.REFUND = { entries: entriesGroupedByType.REFUND };
    }

    const feeProfile = await fetchInvoiceAddress(entries);
    const {
      street = "",
      unit = "",
      city = "",
      state = "",
      country = "",
      zip = "",
    } = feeProfile;

    const regionalFees = await new Promise<RegionalFeesResponse>((resolve) => {
      Fees.fetchRegionalFees(
        token,
        { country: country },
        (fetchedRegionalFees: RegionalFeesResponse) => {
          resolve(fetchedRegionalFees);
        },
      );
    });

    if (regionalFees.statusCode !== 200 || isEmpty(feeProfile)) {
      return;
    }

    const isEUCountry =
      get(regionalFees, "regions[0].code", "") === EU_REGION_CODE;
    const psAddressObj = isEUCountry
      ? (envWeb?.ps_addresses?.[EU_ADDRESS_COUNTRY] ?? "")
      : (envWeb?.ps_addresses?.[country] ?? "");

    const addressFormatter = new AddressFormatter(split(locale, "-", 1)[0]);
    const addressFormat = await addressFormatter.format({
      address1: street,
      address2: unit,
      province: state,
      city,
      zip,
      country,
    });

    const { taxNumberName, taxNumber } = getTaxDetails(country, envWeb);

    const document = (
      <TaxInvoice
        invoiceId={invoiceId}
        date={
          isTransactional
            ? `${month}${day ? ` ${day}` : ""}, ${year}`
            : `${month} ${year}`
        }
        invoiceCountry={countryName}
        invoiceFeeName={feeName}
        intl={intl}
        profile={profileData}
        userFeeProfile={userFeeProfile}
        userAddressFormatArray={addressFormat}
        psAddressObj={psAddressObj}
        invoiceEntriesByType={invoiceEntriesByType}
        businessName={vatBusinessName}
        locale={locale}
        spaceRentals={spaceRentals}
        feeTypes={feeTypes}
        userOrganization={userOrganization}
        taxNumber={taxNumber}
        taxNumberName={taxNumberName}
        // Only the sales-tax invoice prints a contact number.
        contactPhoneNumber={undefined}
      />
    );

    await downloadPdf(
      document,
      `PeerspaceTaxInvoice-${invoiceId}.pdf`,
      revealPdfPage,
    );
  };

  const downloadInvoice = async (request: InvoiceDownloadRequest) => {
    if (request.feeName === US_SALES_TAX_FEE_NAME) {
      return downloadSalesTaxInvoice(request);
    }
    return downloadTaxInvoice(request);
  };

  return { downloadInvoice };
};

export { useInvoiceDownloads };
